import type { Split } from "@/types/split";

/**
 * Splits validation and amount allocation.
 *
 * The invariant a user actually controls is the PERCENTAGE column: amounts are
 * derived as `total * percentage / 100`. Validating the *amount* sum against
 * the total with a fixed cents tolerance is wrong — the rounding error scales
 * with the total magnitude (e.g. COP invoices in the millions), so a fixed
 * `N * 0.01` currency tolerance is meaningless for large or zero-decimal
 * currencies and produces false "Excede total" warnings once a line is split
 * many ways. We validate percentages against 100 instead: that is
 * magnitude-independent and matches the user's intent.
 *
 * Backend mirror: `purchases/utils/currency_rounding.py::adjust_amounts_to_total`
 * (finnecto-api PR #1969) does the same remainder allocation on save.
 */

type WithPercentage = Pick<Split, "percentage">;

/** A row carries something to charge to: its own cost center or account, or a driver. */
export function hasSplitDimension(split: Pick<Split, "cost_center" | "account" | "driver">): boolean {
  return Boolean(split.cost_center || split.account || split.driver);
}

/** What a read-only view shows for a row's target: the driver, else the cost center name. */
export function splitTargetLabel(split: Pick<Split, "cost_center" | "driver">): string {
  if (split.driver) return `${split.driver.code} | ${split.driver.name}`;
  return split.cost_center?.name ?? "";
}

/**
 * A percentage rounded to 2 decimals can be off by up to 0.005pp; the
 * accumulated worst case over N splits is `N * 0.005`pp. We use `N * 0.01`pp
 * (double the theoretical max) as a safe, magnitude-independent tolerance.
 */
export function percentageTolerance(splitCount: number): number {
  return Math.max(splitCount, 1) * 0.01;
}

export function splitsPercentageSum(splits: WithPercentage[]): number {
  return splits.reduce((sum, s) => sum + (s.percentage ?? 0), 0);
}

/**
 * True when the distribution over-allocates beyond rounding tolerance
 * (sum of percentages > 100). Drives the visual "Excede total" warning.
 * Over-allocation only — under-allocation is not "exceeding".
 */
export function splitsExceedTotal(splits: WithPercentage[]): boolean {
  return splitsPercentageSum(splits) > 100 + percentageTolerance(splits.length);
}

/**
 * True when the distribution does not add up to 100% in either direction,
 * beyond rounding tolerance. Use to block saving an incomplete distribution.
 */
export function splitsDistributionInvalid(splits: WithPercentage[]): boolean {
  return (
    Math.abs(splitsPercentageSum(splits) - 100) > percentageTolerance(splits.length)
  );
}

/**
 * Re-derives each split's amount from its percentage and the total, rounds each
 * to the currency's minor-unit precision, then pushes the rounding remainder
 * onto the largest split so the amounts sum EXACTLY to `total`. Eliminates the
 * accumulated-rounding drift that surfaced amounts like `3.137.846,3413` and
 * caused amount-sum validations to overshoot.
 *
 * Splits with no percentage keep a 0 amount. Returns new split objects.
 */
export function allocateSplitAmounts<T extends Split>(
  splits: T[],
  total: number,
  decimals = 2,
): T[] {
  if (!splits.length) return splits;
  const factor = 10 ** decimals;
  const round = (n: number) => Math.round(n * factor) / factor;

  const withAmounts = splits.map((s) => ({
    ...s,
    amount: round((total * (s.percentage ?? 0)) / 100),
  }));

  const allocated = withAmounts.reduce((sum, s) => sum + (s.amount ?? 0), 0);
  const remainder = round(total - allocated);

  if (remainder !== 0) {
    // Push the leftover onto the largest split so a single minor-unit
    // correction is negligible relative to that split's magnitude.
    let largestIdx = 0;
    for (let i = 1; i < withAmounts.length; i++) {
      if ((withAmounts[i].amount ?? 0) > (withAmounts[largestIdx].amount ?? 0)) {
        largestIdx = i;
      }
    }
    withAmounts[largestIdx].amount = round(
      (withAmounts[largestIdx].amount ?? 0) + remainder,
    );
  }

  return withAmounts;
}
